import { useEffect, useMemo } from 'react';
import { Control, Controller, FieldErrors, UseFormSetValue, useWatch } from 'react-hook-form';

import { TextField } from '@/components/shared/TextField/TextField';
import { ExploreBlockType } from '@addmile/backend-client';

import { useContentQueries } from '../../../services/ContentManagerService';
import { BlockFormData } from '../schema';

type MetaFieldsProps = {
  control: Control<BlockFormData>;
  errors: FieldErrors<BlockFormData>;
  isEdit: boolean;
  blockSlug?: string;
  setValue: UseFormSetValue<BlockFormData>;
};

export const MetaFields = ({ control, errors, isEdit, blockSlug, setValue }: MetaFieldsProps) => {
  const { useCollectionsBlocks } = useContentQueries();
  const { data: collectionsBlocksData } = useCollectionsBlocks(blockSlug, !!blockSlug && isEdit);

  const existingCollectionData = useMemo(() => {
    if (!collectionsBlocksData?.items) return { slugs: [], positions: {} };
    const items = collectionsBlocksData.items.filter((item) => item.blockSlug === blockSlug);
    return {
      slugs: items.map((item) => item.collectionSlug),
      positions: Object.fromEntries(
        items.map((item) => [item.collectionSlug, item.position || 1]),
      ),
    };
  }, [collectionsBlocksData, blockSlug]);

  useEffect(() => {
    if (isEdit && blockSlug && collectionsBlocksData !== undefined) {
      setValue('collections', existingCollectionData.slugs, { shouldValidate: false });
      setValue('collectionPositions', existingCollectionData.positions, { shouldValidate: false });
    }
  }, [existingCollectionData, isEdit, blockSlug, setValue, collectionsBlocksData]);
  const type = useWatch({ control, name: 'type' });

  const shouldShowIntro =
    type === ExploreBlockType.Practice ||
    type === ExploreBlockType.Quiz ||
    type === ExploreBlockType.Reflect ||
    type === ExploreBlockType.MythBuster ||
    type === ExploreBlockType.MissingPiece;

  return (
    <>
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-semibold text-neutral-70 mb-2">Slug</label>
          <Controller
            name="slug"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                placeholder="block-slug"
                disabled={isEdit}
                className="!w-full"
                inputClassName="!border !border-neutral-20 !font-medium"
              />
            )}
          />
          {errors.slug && (
            <p className="mt-1 text-sm text-red-600">
              {typeof errors.slug.message === 'string'
                ? errors.slug.message
                : String(errors.slug.message)}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-semibold text-neutral-70 mb-2">Title</label>
          <Controller
            name="title"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                placeholder="Block title"
                className="!w-full"
                inputClassName="!border !border-neutral-20 !font-medium"
              />
            )}
          />
          {errors.title && (
            <p className="mt-1 text-sm text-red-600">
              {typeof errors.title.message === 'string'
                ? errors.title.message
                : String(errors.title.message)}
            </p>
          )}
        </div>
      </div>
      <div className="mb-4">
        <label className="block text-sm font-semibold text-neutral-70 mb-2">Image URL</label>
        <Controller
          name="imageUrl"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              placeholder="https://example.com/image.jpg"
              className="!w-full"
              inputClassName="!border !border-neutral-20 !font-medium"
            />
          )}
        />
      </div>

      {shouldShowIntro && (
        <div className="mb-4">
          <label className="block text-sm font-semibold text-neutral-70 mb-2">Description</label>
          <Controller
            name="intro"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                isTextArea
                placeholder="Type here..."
                className="!w-full"
                inputClassName="!border !border-neutral-20 !font-medium"
              />
            )}
          />
        </div>
      )}
    </>
  );
};

export default MetaFields;
